import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../components/myTrack/AuthContext';
import { rootUrl } from '../../config/config';
import axios from 'axios';
import { Button, Typography } from "@material-tailwind/react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus, faArrowLeft } from '@fortawesome/free-solid-svg-icons'
import DriverSummaryCard from './driver/DriverSummaryCard';
import { Alerty } from './../common/Alerty';
import AssignmentHistory from './driver/allocations/AssignmentHistory';
import AssignDriverForm from './driver/allocations/AssignDriverForm';


const CarDrivers = () => {
    const { user, token } = useAuth();
    const [drivers, setDrivers] = useState([]);
    const [assignedDrivers, setAssignedDrivers] = useState([]);
    const [selectedDriver, setSelectedDriver] = useState('');
    const [assignmentDate, setAssignmentDate] = useState('');
    const [weeklyRate, setWeeklyRate] = useState('');
    const [notes, setNotes] = useState('');
    const [alert, setAlert] = useState({ color: '', message: '' });

    const carPlate = window.location.pathname.split('/').pop();

    const fetchDrivers = useCallback(async () => {
        try {
            // Request a large limit to get all drivers for the dropdown
            const response = await axios.get(`${rootUrl}/drivers?limit=1000`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            console.log("drivers api response:", response.data);
            // Handle paginated response - extract the data array
            const driverData = response.data.data || response.data;
            setDrivers(Array.isArray(driverData) ? driverData : []);
        } catch (error) {
            console.error('Error fetching drivers:', error);
            setDrivers([]); // Set empty array on error to prevent map errors
        }
    }, [token]);

    const fetchWeeklyAmount = useCallback(async () => {
        try {
            const response = await axios.get(`${rootUrl}/system/weekly_amount`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setWeeklyRate(response.data.value);
        } catch (error) {
            console.error('Error fetching weekly amount:', error);
        }
    }, [token]);

    const fetchAssignedDrivers = useCallback(async () => {
        try {
            const response = await axios.get(`${rootUrl}/cars/drivers/plate/${carPlate}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setAssignedDrivers(response.data.data);
        } catch (error) {
            console.error('Error fetching assigned drivers:', error);
        }
    }, [carPlate, token]);

    const handleAssignDriver = async (e) => {
        e.preventDefault();
        if (!selectedDriver || !assignmentDate || !weeklyRate) {
            setAlert({
                color: 'amber', message: 'All fields are required! Affected values: ' +
                    [!selectedDriver && 'Driver', !assignmentDate && 'Assignment Date', !weeklyRate && 'Weekly Rate']
                        .filter(Boolean)
                        .join(', ')
            });
            return;
        }

        // Validate Driver License Status & Expiry
        const driverToAssign = drivers.find(d => String(d.id_no) === String(selectedDriver));
        if (driverToAssign) {
            // Check verification status
            const isVerified = driverToAssign.license_verification_status?.toLowerCase() === 'verified';
            if (!isVerified) {
                setAlert({
                    color: 'red',
                    message: `Cannot assign driver. License is not verified (Status: ${driverToAssign.license_verification_status || 'Unknown'}). Please verify the driver first.`
                });
                return;
            }

            // Check expiry
            if (driverToAssign.license_expiry) {
                const expiryDate = new Date(driverToAssign.license_expiry);
                const today = new Date();
                today.setHours(0, 0, 0, 0); // Compare dates only

                if (expiryDate < today) {
                    setAlert({
                        color: 'red',
                        message: `Cannot assign driver. License expired on ${expiryDate.toLocaleDateString()}.`
                    });
                    return;
                }
            }
        }

        try {
            await axios.post(
                `${rootUrl}/cars/drivers`,
                {
                    plate_no: carPlate,
                    driver_id: selectedDriver,
                    driver_name: driverToAssign?.name || 'Unknown',
                    allocation_date: assignmentDate,
                    input_by: user?.username,
                    weekly_amount: weeklyRate,
                    notes: notes
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setAlert({ color: 'bg-green-500', message: 'Driver assigned successfully!' });
            fetchAssignedDrivers();
            // Reset form
            setSelectedDriver('');
            setWeeklyRate('');
            setNotes('');
        } catch (error) {
            console.error('Error assigning driver:', error);
            setAlert({ color: 'red', message: error.response?.data?.message || 'Failed to assign driver.' });
        }
    };

    useEffect(() => {
        fetchWeeklyAmount();
        fetchDrivers();
        fetchAssignedDrivers();
        setAssignmentDate(new Date().toISOString().split('T')[0]);
    }, [fetchWeeklyAmount, fetchDrivers, fetchAssignedDrivers]);

    const handleDeallocateDriver = async (assignmentId, deallocate_date) => {
        if (!window.confirm('Are you sure you want to deallocate this driver?')) {
            return;
        }
        const editBy = user?.username;

        const payload = {
            end_date: deallocate_date,
            edit_by: editBy,
        };


        try {
            await axios.put(`${rootUrl}/cars/drivers/${assignmentId}/deallocate`, {
                headers: { Authorization: `Bearer ${token}` },
                ...payload,
            });
            setAlert({ color: 'bg-green-500', message: 'Driver deallocated successfully!' });
            fetchAssignedDrivers();
        } catch (error) {
            console.error('Error deallocating driver:', error);
            setAlert({ color: 'red', message: error.response?.data?.message || 'Failed to deallocate driver.' });
        }
    };

    const handleDeleteAssignment = async (assignmentId) => {
        if (!window.confirm('Are you sure you want to delete this assignment?')) {
            return;
        }

        try {
            await axios.delete(`${rootUrl}/cars/drivers/${assignmentId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setAlert({ color: 'bg-green-500', message: 'Assignment deleted successfully!' });
            fetchAssignedDrivers();
        } catch (error) {
            console.error('Error deleting assignment:', error);
            setAlert({ color: 'red', message: error.response?.data?.message || 'Failed to delete assignment.' });
        }
    };



    return (
        <div className="min-h-screen bg-brand-gradient p-4 sm:p-6">
            <div className="max-w-7xl mx-auto">
                <div className="mb-6">
                    <Button
                        variant="text"
                        className="flex items-center gap-2 text-brand-primary hover:bg-brand-primary/10"
                        onClick={() => window.location.href = '/cars'}
                    >
                        <FontAwesomeIcon icon={faArrowLeft} />
                        Back to Cars
                    </Button>
                </div>

                <div className="text-center mb-8">
                    <h1 className="text-3xl font-bold text-brand-secondary mb-2 flex items-center justify-center gap-3">
                        <FontAwesomeIcon icon={faUserPlus} className="text-brand-primary" />
                        Assign Driver
                    </h1>
                    <Typography variant="lead" className="text-gray-600">
                        Plate Number:{' '}
                        <a href={`/cars/${carPlate}`} className="font-bold text-brand-primary hover:underline">
                            {carPlate || ''}
                        </a>
                    </Typography>
                    <div className="w-24 h-1 bg-brand-primary mx-auto rounded-full mt-4"></div>
                </div>

                {
                    alert && alert.message && (
                        <Alerty open={alert.message} setOpen={setAlert} color={alert.color} message={alert.message} />
                    )
                }



                <AssignDriverForm
                    drivers={drivers}
                    selectedDriver={selectedDriver}
                    setSelectedDriver={setSelectedDriver}
                    assignmentDate={assignmentDate}
                    setAssignmentDate={setAssignmentDate}
                    weeklyRate={weeklyRate}
                    setWeeklyRate={setWeeklyRate}
                    notes={notes}
                    setNotes={setNotes}
                    onAssign={handleAssignDriver}
                />

                {
                    selectedDriver && (
                        <div className="mb-8">
                            <DriverSummaryCard drivers={drivers} selectedDriver={selectedDriver} />
                        </div>
                    )
                }

                <AssignmentHistory
                    assignments={assignedDrivers}
                    onDeallocate={handleDeallocateDriver}
                    onDelete={handleDeleteAssignment}
                />

                {/* Car timeline moved to the Cars page Timeline tab */}
            </div>
        </div>
    );
};

export default CarDrivers;